import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  lazy,
  Suspense,
} from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Snackbar,
  Alert,
  Typography,
  Button,
} from "@mui/material";

import axios from "axios";
import qs from "qs";
import dayjs from "dayjs";
import debounce from "lodash/debounce";

import { defaultemailTemplates, tableHeaders } from "../components/staticData";
import { copyToClipboard, timeAgo } from "../components/commonFunctions";
import EmailAction from "../components/EmailAction";
import {
  RoleChip,
  DomainCell,
} from "../components/commonComponents";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeTableSkeleton } from "../components/utilities";
import { DataGrid } from "@mui/x-data-grid";
import FullPageLoader from "../components/FullPageLoader";
const Filters = lazy(() => import("../components/Filters"));

const getQueryParams = () => {
  const params = new URLSearchParams(window.location.search);

  return {
    page: Number(params.get("page")) || 1,
    limit: Number(params.get("limit")) || 20,
    search: params.get("search") || "",
    theme: params.getAll("theme"),
    role: params.getAll("role"),
    country: params.getAll("country"),
    range: params.get("range"),
    startDate: params.get("startDate"),
    endDate: params.get("endDate"),
  };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialQuery = useMemo(() => getQueryParams(), []);
  const [themeData, setThemeData] = useState([]);
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(initialQuery.page);
  const [limit, setLimit] = useState(initialQuery.limit);
  const [total, setTotal] = useState(0);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [hoverField, setHoverField] = useState(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const [filters, setFilters] = useState({
    search: initialQuery.search,
    theme: initialQuery.theme,
    role: initialQuery.role,
    country: initialQuery.country,
    range: initialQuery?.range ?? "",
    selectedDates: {
      startDate: initialQuery?.startDate,
      endDate: initialQuery?.endDate,
    },
  });

  const [toast, setToast] = useState({ success: false, error: false, msg: "" });
  const [copyToast, setCopyToast] = useState(false);
  /* -- FETCH  --*/
  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getsettings`
      );

      // console.log("res.data", res.data);
      if (res.data?.success) setSettings(res.data.data || {});
    } catch (err) {
      console.error("Settings error:", err);
    } finally {
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getalltemplate`
      );
      const templates = response?.data?.data?.templates;

      if (templates) {
        setTemplates(templates);
      } else {
        setTemplates(defaultemailTemplates);
      }
    } catch (err) {
      console.error("Error fetching templates:", err);
    }
  }, []);
  const fetchThemeData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getdata`,
        {
          params: {
            page,
            limit,
            search: filters?.search?.trim(),
            theme: filters?.theme,
            role: filters?.role,
            country: filters?.country,
            range: filters?.range,
            startDate: filters?.selectedDates.startDate
              ? dayjs(filters?.selectedDates.startDate).format("YYYY-MM-DD")
              : null,
            endDate: filters?.selectedDates.endDate
              ? dayjs(filters?.selectedDates.endDate).format("YYYY-MM-DD")
              : null,
          },
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: "repeat" }),
        }
      );

      if (response?.data?.success) {
        setThemeData(response?.data?.data || []);
        setTotal(response?.data?.pagination?.totalItems || 0);
      }
    } catch (err) {
      console.error("Theme error:", err);
      setToast({ error: true, success: false, msg: "Failed to load data" });
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  const debouncedFetchThemeData = useMemo(
    () => debounce(fetchThemeData, 500),
    [fetchThemeData]
  );

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
  }, [fetchSettings, fetchTemplates]);

  useEffect(() => {
    const isSearchActive = filters?.search?.trim().length > 0;
    const isFilterActive =
      filters?.theme?.length > 0 ||
      filters?.role.length > 0 ||
      filters?.country.length > 0 ||
      filters?.range ||
      filters?.selectedDates.startDate ||
      filters?.selectedDates.endDate;

    if (isSearchActive || isFilterActive) {
      debouncedFetchThemeData();
    } else {
      fetchThemeData();
    }

    return () => {
      debouncedFetchThemeData.cancel();
    };
  }, [page, limit, filters, fetchThemeData, debouncedFetchThemeData]);

  useEffect(() => {
    const query = new URLSearchParams();

    const queryParams = {
      page,
      limit,
      search: filters?.search,
      theme: filters?.theme,
      role: filters?.role,
      country: filters?.country,
      range: filters?.range,
      startDate: filters?.selectedDates.startDate,
      endDate: filters?.selectedDates.endDate,
    };

    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => query.append(key, v));
      } else if (value !== null && value !== "" && value !== undefined) {
        query.set(key, value);
      }
    });

    const newQueryString = `?${query.toString()}`;
    if (location.search !== newQueryString) {
      navigate(newQueryString, { replace: true });
    }
  }, [page, limit, filters, navigate, location.search]);

  useEffect(() => {
    const params = getQueryParams();
    setPage(params.page);
    setLimit(params.limit);

    const newFilters = {
      search: params.search,
      theme: params.theme,
      role: params.role,
      country: params.country,
      range: params.range,
      selectedDates: { startDate: params.startDate, endDate: params.endDate },
    };

    if (JSON.stringify(newFilters) !== JSON.stringify(filters)) {
      setFilters(newFilters);
    }
  }, [location.search]);

  const handlePage = useCallback((_, newPage) => setPage(newPage + 1), []);
  const handleLimit = useCallback((e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  }, []);

  const rows = useMemo(() => themeData, [themeData]);

  const columns = [
    {
      field: "domain",
      headerName: "Domain",
      flex: 1,
      width: 300,
      renderCell: (params) => (
        <div
          onMouseEnter={() => {
            setHoverIndex(params.row.index);
            setHoverField("domain");
          }}
          onMouseLeave={() => {
            setHoverIndex(null);
            setHoverField(null);
          }}
        >
          <DomainCell
            field="domain"
            domain={params.value}
            value={params.value}
            themeId={params.row.themeId}
            showWarning={params.row.themeStoreId === null}
            onCopy={(x) => copyToClipboard(x, setCopyToast)}
            index={params.row.index}
            hoverIndex={hoverIndex}
            hoverField={hoverField}
            theme={params.row}
          />
        </div>
      ),
    },
    {
      field: "unSubscribed",
      headerName: "Unsubscribed",
      width: 120,
      renderCell: (params) => <Typography>{params.value?'Yes':'No'}</Typography>,
    },
    {
      field: "themeSchemaName",
      headerName: "Theme Schema",
      width: 200,
      renderCell: (params) => <Typography>{params.value}</Typography>,
    },
    {
      field: "themeVersion",
      headerName: "Version",
      width: 120,
      renderCell: (params) => <Typography>{params.value}</Typography>,
    },
    {
      field: "themeRole",
      headerName: "Role",
      width: 140,
      renderCell: (params) => <RoleChip role={params.value} />,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      renderCell: (params) => (
        <div
          onMouseEnter={() => {
            setHoverIndex(params.row.index);
            setHoverField("email");
          }}
          onMouseLeave={() => {
            setHoverIndex(null);
            setHoverField(null);
          }}
        >
        <DomainCell
          field="email"
          value={params.value}
          onCopy={(x) => copyToClipboard(x, setCopyToast)}
          index={params.row.index}
          hoverIndex={hoverIndex}
          hoverField={hoverField}
        /></div>
      ),
    },
    {
      field: "region",
      headerName: "Region",
      width: 140,
      renderCell: (params) => <Typography>{params.value}</Typography>,
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 140,
      renderCell: (params) => <Typography>{timeAgo(params.value)}</Typography>,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <EmailAction
          theme={params.row}
          themeData={themeData}
          setThemeData={setThemeData}
          fetchThemeData={fetchThemeData}
          setToast={setToast}
          setLoading={setLoading}
          loading={loading}
          limit={limit}
          settings={settings}
          templates={templates}
        />
      ),
    },
  ];
  

  const rows2 = themeData?.map((theme, index) => ({
    id: theme._id,
    index: index,
    ...theme,
    theme: theme,
  }));
  const handleRowSelection =(e)=>{
    console.log(e)
  }
  return (
    <Stack spacing={2}>
      {/* <Button
        size="small"
        onClick={async (e) => {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}/v1/metrics`,
            {
              shopName: "priti_testing",
              domain: "priti.myshopify.com",
              themeSchemaName: "King4",
              themeName: "mytheme",
              email: "pallavi.priti@shinedezign.com",
              themeId: 145577456,
              themeRole: "unpublished",
              themeVersion: "1.2.05",
              themeStoreId: 70888194225,
              region: "MMMMMMMMMMM",
              route: "/",
              isThemeEditor: true,
            }
          );

          console.log("data", res);
          fetchThemeData();
        }}
      >
        Track Theme
      </Button>  */}
      <Suspense fallback={<FullPageLoader/>}>
        <Filters
          filters={filters}
          setFilters={setFilters}
          fetchThemeData={() => {}}
          setPage={setPage}
          total={total}
          setLimit={setLimit}
          page={page}
          settings={settings}
          limit={limit}
          themeData={themeData}
        />
      </Suspense>

      <>
        {/* Loading → Skeleton */}
        {loading && <ThemeTableSkeleton tableHeaders={tableHeaders} />}

        {/* Loaded → No results */}
        {!loading && rows?.length === 0 && (
          <TableContainer
            className="card main-content-table"
            sx={{ height: "75vh" }}
            component={Box}
          >
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableCell key={header} sx={{ fontWeight: 600 }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={tableHeaders?.length}
                    align="center"
                    sx={{ padding: "50px" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        color: "text.secondary",
                        mt: "20px",
                      }}
                    >
                      <img
                        src="/noData.svg"
                        alt="No Data"
                        style={{
                          width: "20%",
                          height: "20%",
                          objectFit: "contain",
                          opacity: 0.8,
                        }}
                      />
                      <Typography fontWeight={400} fontSize="0.95rem">
                        No records to display. Modify your filters to view
                        results.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Loaded → Data table */}
        { !loading && rows?.length > 0 && (
          <>
            <TableContainer
              className="card main-content-table"
              sx={{ height: "75vh" }}
              component={Box}
            >
              <Table stickyHeader aria-label="sticky table" size="small">
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableCell key={header} sx={{ fontWeight: 600 }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows?.map((theme, index) => (
                    <TableRow key={theme?._id || index} hover>
                      <TableCell
                        onMouseEnter={() => {
                          setHoverIndex(index);
                          setHoverField("domain");
                        }}
                        onMouseLeave={() => {
                          setHoverIndex(null);
                          setHoverField(null);
                        }}
                      >
                        <DomainCell
                          field={"domain"}
                          domain={theme?.domain}
                          value={theme?.domain}
                          themeId={theme?.themeId}
                          showWarning={theme?.themeStoreId === null}
                          onCopy={(x) => copyToClipboard(x, setCopyToast)}
                          index={index}
                          hoverIndex={hoverIndex}
                          hoverField={hoverField}
                          // theme={theme}
                        />
                      </TableCell>

                      <TableCell>{theme?.unSubscribed?'Yes':'No'}</TableCell>
                      <TableCell>{theme?.themeSchemaName}</TableCell>
                      <TableCell>{theme?.themeVersion}</TableCell>

                      <TableCell>
                        <RoleChip role={theme?.themeRole} />
                      </TableCell>

                      <TableCell
                        onMouseEnter={() => {
                          setHoverIndex(index);
                          setHoverField("email");
                        }}
                        onMouseLeave={() => {
                          setHoverIndex(null);
                          setHoverField(null);
                        }}
                      >
                        <DomainCell
                          field={"email"}
                          value={theme?.email}
                          onCopy={(x) => copyToClipboard(x, setCopyToast)}
                          index={index}
                          hoverIndex={hoverIndex}
                          hoverField={hoverField}
                        />
                      </TableCell>

                      <TableCell>{theme?.region}</TableCell>
                      <TableCell sx={{ minWidth: "130px" }}>
                        {timeAgo(theme?.createdAt)}
                      </TableCell>

                      <TableCell>
                        <EmailAction
                          index={index}
                          theme={theme}
                          themeData={themeData}
                          setThemeData={setThemeData}
                          fetchThemeData={fetchThemeData} // Pass the optimized fetch function
                          setToast={setToast}
                          setLoading={setLoading}
                          loading={loading}
                          limit={limit}
                          settings={settings}
                          templates={templates}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        {/* {!loading && rows?.length > 0 && (
          <>
            <Box className="card main-content-table" sx={{ height: "75vh" }}>
              <DataGrid
                loading={loading}
                rows={rows2}
                columns={columns}
                hideFooterPagination
                hideFooter
                // checkboxSelection
                disableRowSelectionOnClick
                onClipboardCopy={(e) => {
                  console.log(e);
                }}
                // rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(newSelection) => {
                  setSelectedRowIds(newSelection);
                  handleRowSelection(newSelection);
                }}
                checkboxSelectionVisibleOnly={false} // IMPORTANT
              />
            </Box>
          </>
        )} */}

        {rows?.length > 0 && (
          <TablePagination
            sx={{
              boxShadow: " 0px -1px 3px rgba(0, 0, 0, 0.08);",
            }}
            component="div"
            count={total}
            page={page - 1}
            rowsPerPage={limit}
            onPageChange={handlePage}
            onRowsPerPageChange={handleLimit}
            rowsPerPageOptions={[20, 50, 100]}
          />
        )}
      </>
      {/* Toasts */}
      <Snackbar
        open={toast.success}
        autoHideDuration={800}
        onClose={() => setToast({ ...toast, success: false, msg: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{toast.msg}</Alert>
      </Snackbar>

      <Snackbar
        open={toast.error}
        autoHideDuration={1200}
        onClose={() => setToast({ ...toast, error: false, msg: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{toast.msg}</Alert>
      </Snackbar>

      <Snackbar
        open={copyToast}
        autoHideDuration={600}
        onClose={() => setCopyToast(false)}
        message="Copied!"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Stack>
  );
};

export default Dashboard;
